/* ==========================================================================
   Cilabri — socle d'animations GSAP (partagé par toutes les pages)
   Plugins : ScrollTrigger, CustomEase, SplitText, ScrollToPlugin.
   Le site modèle n'a presque aucun mouvement : transitions CSS de 0,125 à 0,5 s
   (restées en CSS) et une seule mise en scène liée au défilement — le pied de page « rideau » (A3).
   Ce fichier fournit :
     - les easings du design system (CustomEase) ;
     - window.CG : { mm, ease, reduce, fx } pour les scripts de page ;
     - A3 : pied de page rideau, piloté par la variable CSS --p (scrub) ;
     - les apparitions au défilement via [data-reveal] ;
     - l'accordéon des FAQ ([data-accordion]) : hauteur 0,45 s easeOutCubic ;
     - gsap.matchMedia : prefers-reduced-motion et breakpoints 749 / 989 px.
   ========================================================================== */
(function () {
  "use strict";
  var CG = (window.CG = window.CG || {});
  var REDUCE_QUERY = "(prefers-reduced-motion: reduce)";
  CG.reduce = function () { return window.matchMedia(REDUCE_QUERY).matches; };

  if (!window.gsap) { return; }
  var gsap = window.gsap;
  gsap.registerPlugin(ScrollTrigger, CustomEase, SplitText, ScrollToPlugin);
  ScrollTrigger.config({ ignoreMobileResize: true });

  /* ---------- Easings du design system ---------- */
  CustomEase.create("cgStandard", ".4,0,.2,1");        // Material standard
  CustomEase.create("cgSoft", ".45,0,.2,1");
  CustomEase.create("cgQuint", ".22,1,.36,1");         // easeOutQuint : pilule, transforms
  CustomEase.create("cgCubic", ".33,1,.68,1");         // easeOutCubic : accordéons, chevrons
  CustomEase.create("cgSwift", ".2,.8,.2,1");
  CustomEase.create("cgInOut", ".5,0,.2,1");
  CustomEase.create("cgDrawer", ".32,.72,0,1");
  CustomEase.create("cgQuad", ".25,.46,.45,.94");
  CG.ease = {
    standard: "cgStandard", soft: "cgSoft", quint: "cgQuint", cubic: "cgCubic",
    swift: "cgSwift", inOut: "cgInOut", drawer: "cgDrawer", quad: "cgQuad", ease: "power1.inOut"
  };
  CG.dur = { fast: 0.2, base: 0.3, slow: 0.45, long: 0.5 };

  /* ---------- gsap.matchMedia ---------- */
  var mm = (CG.mm = gsap.matchMedia());
  CG.queries = {
    reduce: REDUCE_QUERY,
    motion: "(prefers-reduced-motion: no-preference)",
    mobile: "(max-width: 749px)",
    tablet: "(min-width: 750px) and (max-width: 989px)",
    desktop: "(min-width: 990px)"
  };

  /* Les scripts de page appellent CG.ready(fn) : fn reçoit { context, conditions } de gsap.matchMedia */
  CG.ready = function (fn) { return mm.add(CG.queries, fn); };

  /* ---------- Bibliothèque d'effets réutilisables ---------- */
  CG.fx = {
    /* apparition : fondu + montée de 16 px */
    reveal: function (targets, opts) {
      opts = opts || {};
      return gsap.fromTo(targets,
        { opacity: 0, y: opts.y == null ? 16 : opts.y },
        { opacity: 1, y: 0, duration: opts.duration || 0.7, delay: opts.delay || 0, stagger: opts.stagger || 0,
          ease: "cgQuint", clearProps: "transform,opacity,willChange" });
    }
  };

  /* ---------- A3 — Pied de page « rideau » ----------
     Déclencheur : défilement lié (scrub). --p passe de 0 à 1 sur la hauteur de la scène (432 / 240 / 220 px) :
     0 quand le haut de la scène atteint le bas de la fenêtre, 1 en bas de page. Courbe linéaire.
     Les règles CSS (styles.css, .cg-footer-reveal) lisent --p :
       image : scale(calc(1.14 - var(--p) * .14)), origine 50 % 100 %
       lettres : --lp = clamp(0, p*1.9 - i*.11, 1) → translateY(105 % → 0), rotation 8° → 0, opacité .2 → 1 */
  mm.add({ motion: CG.queries.motion, reduce: CG.queries.reduce }, function (ctx) {
    var reduce = ctx.conditions.reduce;
    document.querySelectorAll("[data-footer-reveal]").forEach(function (el) {
      if (reduce) { gsap.set(el, { "--p": 1 }); return; }
      gsap.fromTo(el, { "--p": 0 }, {
        "--p": 1, ease: "none",
        scrollTrigger: { trigger: el, start: "top bottom", end: "bottom bottom", scrub: true, invalidateOnRefresh: true }
      });
    });
  });

  /* ---------- Apparitions au défilement : [data-reveal] ----------
     data-reveal="up|fade|left|right|scale" (défaut up) · data-reveal-delay="0.1" (s)
     Groupe : [data-reveal-group] + data-reveal-stagger="0.08" → ses enfants directs apparaissent en cascade.
     Déclencheur : le haut de l'élément atteint 92 % de la hauteur de la fenêtre, une seule fois. */
  mm.add(CG.queries.motion, function () {
    var shown = new WeakSet();
    function fromVars(kind) {
      var v = { opacity: 0 };
      if (kind === "up" || !kind) v.y = 16;
      else if (kind === "left") v.x = -24;
      else if (kind === "right") v.x = 24;
      else if (kind === "scale") v.scale = 0.96;
      return v;
    }
    function play(targets, delay, stagger, key) {
      if (shown.has(key)) return;
      shown.add(key);
      gsap.to(targets, { opacity: 1, x: 0, y: 0, scale: 1, duration: 0.7, delay: delay, stagger: stagger,
        ease: "cgQuint", clearProps: "transform,opacity,willChange" });
    }
    var units = [];
    document.querySelectorAll("[data-reveal-group]").forEach(function (group) {
      var kids = Array.prototype.slice.call(group.children);
      if (!kids.length) return;
      gsap.set(kids, fromVars(group.getAttribute("data-reveal") || "up"));
      units.push({ trigger: group, run: function () {
        play(kids, parseFloat(group.getAttribute("data-reveal-delay")) || 0,
          parseFloat(group.getAttribute("data-reveal-stagger")) || 0.08, group);
      } });
    });
    document.querySelectorAll("[data-reveal]").forEach(function (el) {
      if (el.hasAttribute("data-reveal-group") || (el.parentElement && el.parentElement.hasAttribute("data-reveal-group"))) return;
      gsap.set(el, fromVars(el.getAttribute("data-reveal")));
      units.push({ trigger: el, run: function () {
        play(el, parseFloat(el.getAttribute("data-reveal-delay")) || 0, 0, el);
      } });
    });
    units.forEach(function (u) {
      ScrollTrigger.create({ trigger: u.trigger, start: "top 92%", once: true, onEnter: u.run });
    });
    /* Filet de sécurité : ce qui est déjà au-dessus de la ligne de déclenchement au chargement apparaît tout de suite. */
    function sweep() {
      units.forEach(function (u) {
        if (u.trigger.getBoundingClientRect().top < window.innerHeight * 0.92) u.run();
      });
    }
    window.addEventListener("load", function () { requestAnimationFrame(sweep); });
  });

  /* ---------- Accordéon FAQ : [data-accordion] ----------
     Clic sur <summary> : hauteur 0 ↔ auto en 0,45 s, easeOutCubic ; le chevron (CSS) tourne en 0,25 s. */
  document.querySelectorAll("[data-accordion]").forEach(function (root) {
    var single = root.hasAttribute("data-accordion-single");
    function close(d, instant) {
      var panel = d.querySelector(".cg-faq__a");
      if (!d.open) return;
      if (instant || CG.reduce() || !panel) { d.open = false; return; }
      d.classList.add("is-closing");
      gsap.to(panel, { height: 0, duration: CG.dur.slow, ease: "cgCubic", onComplete: function () {
        d.open = false; d.classList.remove("is-closing"); gsap.set(panel, { clearProps: "height" });
      } });
    }
    function open(d) {
      var panel = d.querySelector(".cg-faq__a");
      d.classList.remove("is-closing");
      if (d.open && panel) gsap.killTweensOf(panel);
      d.open = true;
      if (CG.reduce() || !panel) return;
      gsap.fromTo(panel, { height: 0 }, { height: "auto", duration: CG.dur.slow, ease: "cgCubic", clearProps: "height" });
    }
    root.querySelectorAll("details").forEach(function (d) {
      var summary = d.querySelector("summary");
      if (!summary) return;
      summary.addEventListener("click", function (e) {
        e.preventDefault();
        if (d.open && !d.classList.contains("is-closing")) { close(d); return; }
        if (single) root.querySelectorAll("details[open]").forEach(function (o) { if (o !== d) close(o); });
        open(d);
      });
    });
  });

  /* ---------- Header au défilement ----------
     Sur le site modèle l'en-tête est collant, ne se masque pas et garde le même aspect (fond, hauteur, pas d'ombre) :
     aucune animation à reproduire. main.js pose seulement data-scrolled sur <header>. */

  /* Rafraîchir les positions une fois les polices et les images prêtes */
  window.addEventListener("load", function () { ScrollTrigger.refresh(); });
  if (document.fonts && document.fonts.ready) { document.fonts.ready.then(function () { ScrollTrigger.refresh(); }); }
})();
